import React, { useState, useEffect } from "react";
import { TimePicker } from "../../../../../components/inputs/MomentDatePicker";
import {
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Card,
  Modal,
  Descriptions,
  Spin,
  Empty,
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { serverUrl } from "../../../../../nestserver";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../../redux/slices/auth.slice";
import dayjs from "dayjs";

const Settings = () => {
  const user = useSelector(selectUser);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [settingsData, setSettingsData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setFetchLoading(true);
    try {
      const response = await axios({
        method: "get",
        url: `${serverUrl}/school-location`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status >= 200 && response.status < 300 && response.data) {
        setSettingsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      if (error?.response?.status === 404) {
        setSettingsData(null);
      }
    } finally {
      setFetchLoading(false);
    }
  };

  const showModal = (editMode = false) => {
    setIsEditMode(editMode);
    setIsModalVisible(true);

    if (editMode && settingsData) {
      const checkInTime = settingsData.check_in_time
        ? dayjs(`2000-01-01 ${settingsData.check_in_time}`, "YYYY-MM-DD HH:mm")
        : null;
      const checkOutTime = settingsData.check_out_time
        ? dayjs(`2000-01-01 ${settingsData.check_out_time}`, "YYYY-MM-DD HH:mm")
        : null;

      form.setFieldsValue({
        latitude: settingsData.latitude,
        longitude: settingsData.longitude,
        range: settingsData.radius_meters !== undefined ? settingsData.radius_meters : settingsData.radius,
        checkInTime: checkInTime,
        checkOutTime: checkOutTime,
        bufferTime: settingsData.buffer_time,
      });
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const schoolId = user?.school?._id;

      if (!schoolId) {
        throw new Error("School ID not found in user data");
      }

      const checkInTime = values.checkInTime
        ? values.checkInTime.format("HH:mm")
        : null;
      const checkOutTime = values.checkOutTime
        ? values.checkOutTime.format("HH:mm")
        : null;

      const payload = {
        schoolId: schoolId,
        school_name: user?.school?.name || "",
        longitude: parseFloat(values.longitude),
        latitude: parseFloat(values.latitude),
        radius: parseFloat(values.range),
        radius_meters: parseFloat(values.range),
        check_in_time: checkInTime,
        check_out_time: checkOutTime,
        buffer_time: parseInt(values.bufferTime, 10),
      };

      const response = await axios({
        method: "put",
        url: `${serverUrl}/school-location`,
        data: payload,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });

      if (response.status >= 200 && response.status < 300) {
        message.success(
          isEditMode
            ? "Settings updated successfully"
            : "Settings added successfully",
        );

        // Close modal and refresh data
        setIsModalVisible(false);
        form.resetFields();
        fetchSettings();
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      if (error?.response?.data?.detail) {
        if (typeof error.response.data.detail === "string") {
          message.error(error.response.data.detail);
        } else if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail
            .map((err) => `${err.loc?.join(".")}: ${err.msg}`)
            .join(", ");
          message.error(`Validation errors: ${errorMessages}`);
        } else {
          message.error(JSON.stringify(error.response.data.detail));
        }
      } else if (error?.message) {
        message.error(error.message);
      } else {
        message.error("Failed to save settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    // Parse time string properly by creating a full date-time string
    const parsed = dayjs(`2000-01-01 ${time}`, "YYYY-MM-DD HH:mm");
    return parsed.isValid() ? parsed.format("h:mm A") : "Invalid Time";
  };

  return (
    <div className="p-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="flex justify-between items-center pb-6">
          <div className="text-2xl text-orange-500 font-semibold">
            Instructor Attendance Settings
          </div>
          {settingsData && (
            <Button
              type="primary"
              onClick={() => showModal(true)}
              size="large"
              style={{ display: "inline-flex", alignItems: "center"}}
            >
              <EditOutlined />
              <span>Edit Settings</span>
            </Button>
          )}
        </div>

        {fetchLoading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : settingsData ? (
          <Card
            className="max-w-4xl"
            style={{ borderRadius: "8px" }}
            bordered={false}
          >
            <Descriptions
              bordered
              column={{ xs: 1, sm: 1, md: 2 }}
              labelStyle={{ fontWeight: 600, backgroundColor: "#fafafa" }}
            >
              <Descriptions.Item label="Latitude">
                {settingsData.latitude}
              </Descriptions.Item>
              <Descriptions.Item label="Longitude">
                {settingsData.longitude}
              </Descriptions.Item>
              <Descriptions.Item label="Range">
                {settingsData.radius_meters !== undefined ? settingsData.radius_meters : settingsData.radius} meters
              </Descriptions.Item>
              <Descriptions.Item label="School Start Time">
                {formatTime(settingsData.check_in_time)}
              </Descriptions.Item>
              <Descriptions.Item label="School End Time">
                {formatTime(settingsData.check_out_time)}
              </Descriptions.Item>
              <Descriptions.Item label="Time Threshold">
                {settingsData.buffer_time} minutes
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : (
          <div className="max-w-4xl ">
            <Empty
              description="No settings configured yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                onClick={() => showModal(false)}
                size="large"
                style={{ display: "inline-flex", alignItems: "center"}}
              >
                <PlusOutlined />
                <span>Add Settings</span>
              </Button>
            </Empty>
          </div>
        )}

        {/* Modal for Add/Edit Form */}
        <Modal
          title={
            <div className="text-xl font-semibold">
              {isEditMode ? "Edit Settings" : "Add Settings"}
            </div>
          }
          visible={isModalVisible}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={700}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            name="attendance_settings"
            onFinish={onFinish}
            autoComplete="off"
            className="mt-4"
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Latitude"
                  name="latitude"
                  rules={[
                    {
                      required: true,
                      message: "Latitude is required",
                    },
                    {
                      validator: (_, value) => {
                        if (value && (value < -90 || value > 90)) {
                          return Promise.reject(
                            "Latitude must be between -90 and 90",
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="number"
                    step="any"
                    placeholder="Enter latitude (e.g., 12.9716)"
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Longitude"
                  name="longitude"
                  rules={[
                    {
                      required: true,
                      message: "Longitude is required",
                    },
                    {
                      validator: (_, value) => {
                        if (value && (value < -180 || value > 180)) {
                          return Promise.reject(
                            "Longitude must be between -180 and 180",
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="number"
                    step="any"
                    placeholder="Enter longitude (e.g., 77.5946)"
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Range (in meters)"
              name="range"
              rules={[
                {
                  required: true,
                  message: "Range is required",
                },
                {
                  validator: (_, value) => {
                    if (value && value < 0) {
                      return Promise.reject("Range must be a positive number");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                step="any"
                min={0}
                placeholder="Enter range in meters (e.g., 100)"
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="School Start Time"
                  name="checkInTime"
                  rules={[
                    {
                      required: true,
                      message: "School start time is required",
                    },
                  ]}
                >
                  <TimePicker
                    format="h:mm a"
                    use12Hours
                    className="w-full"
                    placeholder="Select school start time"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="School End Time"
                  name="checkOutTime"
                  rules={[
                    {
                      required: true,
                      message: "School end time is required",
                    },
                  ]}
                >
                  <TimePicker
                    format="h:mm a"
                    use12Hours
                    className="w-full"
                    placeholder="Select school end time"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Time Threshold (in Minutes)"
              name="bufferTime"
              rules={[
                {
                  required: true,
                  message: "Threshold is required",
                },
                {
                  validator: (_, value) => {
                    if (
                      value &&
                      (value < 0 || !Number.isInteger(Number(value)))
                    ) {
                      return Promise.reject(
                        "Threshold must be a positive integer",
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                min={0}
                step="1"
                placeholder="Enter threshold time in minutes (e.g., 30)"
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>

            <Form.Item className="mb-0 mt-6">
              <div className="flex justify-end gap-4">
                <Button onClick={handleCancel} size="large">
                  Cancel
                </Button>
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  className="text-white"
                  loading={loading}
                >
                  {isEditMode ? "Update" : "Submit"}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Settings;
